import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Add, Delete, Edit } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { elegibilidadeService } from '@/services/produtoService'
import { SecaoProduto } from '@/components/produtos/SecaoProduto'
import {
  OperadorElegibilidade,
  PerguntaElegibilidade,
  PerguntaElegibilidadeRequest,
  TAMANHO_MAXIMO_RESPOSTA_TEXTO,
  TipoRespostaElegibilidade,
} from '@/types/produto'

/**
 * Cadastro das perguntas de elegibilidade de um produto (Etapa 7 da jornada do Consultor).
 *
 * **Por que isto é uma tela e não um arquivo de configuração.** O PDF pede o motor —
 * perguntas ao consultor, resposta com o motivo — mas nunca lista as perguntas. Elas variam
 * por produto e mudam com a política comercial. Cadastradas aqui, o time preenche e ajusta
 * sem passar por desenvolvimento; escritas em código, cada ajuste viraria um deploy.
 *
 * Produto sem nenhuma pergunta continua elegível por valor e prazo — ausência de regra não
 * vira recusa (confirmado pelo time comercial em 04/08).
 */

const TIPOS: { valor: TipoRespostaElegibilidade; rotulo: string }[] = [
  { valor: 'SIM_NAO', rotulo: 'Sim / Não' },
  { valor: 'NUMERO', rotulo: 'Número' },
  { valor: 'ESCOLHA', rotulo: 'Escolha de lista' },
  { valor: 'TEXTO', rotulo: 'Texto livre (até 200 caracteres)' },
]

const OPERADORES: { valor: OperadorElegibilidade; rotulo: string; numerico: boolean }[] = [
  { valor: 'IGUAL_A', rotulo: 'é igual a', numerico: false },
  { valor: 'DIFERENTE_DE', rotulo: 'é diferente de', numerico: false },
  { valor: 'UM_DE', rotulo: 'está na lista', numerico: false },
  { valor: 'NENHUM_DE', rotulo: 'não está na lista', numerico: false },
  { valor: 'MAIOR_OU_IGUAL', rotulo: 'é maior ou igual a', numerico: true },
  { valor: 'MENOR_OU_IGUAL', rotulo: 'é menor ou igual a', numerico: true },
  { valor: 'ENTRE', rotulo: 'está entre', numerico: true },
]

const VAZIA: PerguntaElegibilidadeRequest = {
  ordem: 0,
  enunciado: '',
  tipoResposta: 'SIM_NAO',
  operador: 'IGUAL_A',
  valorEsperado: 'true',
  mensagemRecusa: '',
  bloqueia: true,
  ativo: true,
}

const rotuloOperador = (op: OperadorElegibilidade): string =>
  OPERADORES.find((o) => o.valor === op)?.rotulo ?? op

interface Props {
  produtoId: string
  /** Somente ADMIN edita; os demais veem as perguntas que o consultor vai responder. */
  podeEditar: boolean
}

const PerguntasElegibilidade: React.FC<Props> = ({ produtoId, podeEditar }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [perguntas, setPerguntas] = useState<PerguntaElegibilidade[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [editando, setEditando] = useState<PerguntaElegibilidade | null>(null)
  const [form, setForm] = useState<PerguntaElegibilidadeRequest>(VAZIA)
  const [aberto, setAberto] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      // `false` traz também as desativadas — o ADMIN precisa vê-las para reativar.
      setPerguntas(await elegibilidadeService.perguntas(produtoId, !podeEditar))
    } catch {
      setErro('Não foi possível carregar as perguntas de elegibilidade.')
    } finally {
      setCarregando(false)
    }
  }, [produtoId, podeEditar])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const abrirNova = () => {
    setEditando(null)
    setForm({ ...VAZIA, ordem: perguntas.length })
    setAberto(true)
  }

  const abrirEdicao = (p: PerguntaElegibilidade) => {
    setEditando(p)
    setForm({
      ordem: p.ordem,
      enunciado: p.enunciado,
      tipoResposta: p.tipoResposta,
      opcoes: p.opcoes.join('\n'),
      operador: p.operador,
      valorEsperado: p.valorEsperado ?? '',
      valorMinimo: p.valorMinimo ?? null,
      valorMaximo: p.valorMaximo ?? null,
      mensagemRecusa: p.mensagemRecusa,
      bloqueia: p.bloqueia,
      ativo: p.ativo,
    })
    setAberto(true)
  }

  const salvar = async () => {
    setSalvando(true)
    try {
      if (editando) {
        await elegibilidadeService.atualizarPergunta(produtoId, editando.id, form)
      } else {
        await elegibilidadeService.criarPergunta(produtoId, form)
      }
      enqueueSnackbar('Pergunta salva.', { variant: 'success' })
      setAberto(false)
      await carregar()
    } catch (e) {
      // O backend recusa regra que o motor não conseguiria avaliar; a mensagem dele é
      // mais útil que um "falhou" genérico.
      const msg = e instanceof Error ? e.message : 'Falha ao salvar a pergunta.'
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const remover = async (p: PerguntaElegibilidade) => {
    try {
      await elegibilidadeService.removerPergunta(produtoId, p.id)
      enqueueSnackbar('Pergunta removida.', { variant: 'success' })
      await carregar()
    } catch {
      enqueueSnackbar('Falha ao remover a pergunta.', { variant: 'error' })
    }
  }

  const operadorNumerico = OPERADORES.find((o) => o.valor === form.operador)?.numerico ?? false
  // Texto livre (09/09): o motor só cobra a resposta; não há regra de corte nem recusa a
  // cadastrar — quem lê o texto é o time Originei, nas notas da reserva.
  const textoLivre = form.tipoResposta === 'TEXTO'

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando perguntas de elegibilidade" />
      </Box>
    )
  }

  return (
    <SecaoProduto
      titulo="Perguntas de elegibilidade"
      descricao="Perguntas adicionais respondidas pelo consultor ao lançar a reserva. A regra de faturamento e LTV fica na seção acima; sem perguntas, ela é o único critério."
      acao={
        podeEditar ? (
          <Button variant="outlined" startIcon={<Add />} onClick={abrirNova}>
            Nova pergunta
          </Button>
        ) : undefined
      }
    >

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {perguntas.length === 0 ? (
        <Alert severity="info">
          Nenhuma pergunta cadastrada — a elegibilidade deste produto é só a regra de
          faturamento e LTV acima.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {perguntas.map((p) => (
            <Card key={p.id} sx={{ p: 2, opacity: p.ativo ? 1 : 0.55 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ pr: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {p.enunciado}
                  </Typography>
                  {p.tipoResposta === 'TEXTO' ? (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Texto livre (até {TAMANHO_MAXIMO_RESPOSTA_TEXTO} caracteres) — sem regra de corte; a
                      Originei lê a resposta na reserva.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Passa se a resposta {rotuloOperador(p.operador)}{' '}
                        {p.operador === 'ENTRE'
                          ? `${p.valorMinimo} e ${p.valorMaximo}`
                          : p.valorEsperado ?? p.valorMinimo ?? p.valorMaximo}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Recusa: “{p.mensagemRecusa}”
                      </Typography>
                    </>
                  )}
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    size="small"
                    label={
                      p.tipoResposta === 'TEXTO'
                        ? p.bloqueia ? 'Obrigatória' : 'Opcional'
                        : p.bloqueia ? 'Bloqueia' : 'Só alerta'
                    }
                    color={p.bloqueia ? 'error' : 'warning'}
                    variant="outlined"
                  />
                  {!p.ativo && <Chip size="small" label="Inativa" />}
                  {podeEditar && (
                    <>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEdicao(p)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton size="small" onClick={() => void remover(p)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editando ? 'Editar pergunta' : 'Nova pergunta'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Pergunta"
              value={form.enunciado}
              onChange={(e) => setForm({ ...form, enunciado: e.target.value })}
              placeholder="A empresa vende a prazo para outras empresas?"
              fullWidth
              multiline
            />

            <TextField
              label="Tipo de resposta"
              select
              value={form.tipoResposta}
              onChange={(e) =>
                setForm({ ...form, tipoResposta: e.target.value as TipoRespostaElegibilidade })
              }
              fullWidth
            >
              {TIPOS.map((t) => (
                <MenuItem key={t.valor} value={t.valor}>
                  {t.rotulo}
                </MenuItem>
              ))}
            </TextField>

            {form.tipoResposta === 'ESCOLHA' && (
              <TextField
                label="Opções (uma por linha)"
                value={form.opcoes ?? ''}
                onChange={(e) => setForm({ ...form, opcoes: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
            )}

            {textoLivre && (
              <Alert severity="info">
                O consultor digita um texto de até {TAMANHO_MAXIMO_RESPOSTA_TEXTO} caracteres. Não há
                regra de corte: a resposta vai nas notas da reserva para o time Originei ler.
              </Alert>
            )}

            {!textoLivre && (
            <>
            <TextField
              label="Passa se a resposta…"
              select
              value={form.operador}
              onChange={(e) =>
                setForm({ ...form, operador: e.target.value as OperadorElegibilidade })
              }
              fullWidth
            >
              {OPERADORES.map((o) => (
                <MenuItem key={o.valor} value={o.valor}>
                  {o.rotulo}
                </MenuItem>
              ))}
            </TextField>

            {operadorNumerico ? (
              <Stack direction="row" spacing={2}>
                {(form.operador === 'MAIOR_OU_IGUAL' || form.operador === 'ENTRE') && (
                  <TextField
                    label="Valor mínimo"
                    type="number"
                    value={form.valorMinimo ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, valorMinimo: e.target.value === '' ? null : Number(e.target.value) })
                    }
                    fullWidth
                  />
                )}
                {(form.operador === 'MENOR_OU_IGUAL' || form.operador === 'ENTRE') && (
                  <TextField
                    label="Valor máximo"
                    type="number"
                    value={form.valorMaximo ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, valorMaximo: e.target.value === '' ? null : Number(e.target.value) })
                    }
                    fullWidth
                  />
                )}
              </Stack>
            ) : (
              <TextField
                label={
                  form.operador === 'UM_DE' || form.operador === 'NENHUM_DE'
                    ? 'Lista de comparação (um item por linha)'
                    : 'Valor de comparação'
                }
                value={form.valorEsperado ?? ''}
                onChange={(e) => setForm({ ...form, valorEsperado: e.target.value })}
                multiline={form.operador === 'UM_DE' || form.operador === 'NENHUM_DE'}
                fullWidth
              />
            )}
            </>
            )}

            {!textoLivre && (
            <TextField
              label="Mensagem de recusa"
              value={form.mensagemRecusa}
              onChange={(e) => setForm({ ...form, mensagemRecusa: e.target.value })}
              placeholder="Este produto exige faturamento anual acima de R$ 5 milhões."
              helperText="É o motivo que o consultor vê. Sem ele, a recusa seria um “não” sem explicação."
              fullWidth
              multiline
            />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={form.bloqueia ?? true}
                  onChange={(e) => setForm({ ...form, bloqueia: e.target.checked })}
                />
              }
              label={
                textoLivre
                  ? form.bloqueia ?? true
                    ? 'Resposta obrigatória para lançar a reserva'
                    : 'Resposta opcional'
                  : form.bloqueia ?? true
                    ? 'Bloqueia o lançamento da reserva'
                    : 'Apenas alerta — o consultor pode seguir'
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.ativo ?? true}
                  onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                />
              }
              label="Pergunta ativa"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => void salvar()}
            disabled={salvando || !form.enunciado.trim() || (!textoLivre && !form.mensagemRecusa.trim())}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </SecaoProduto>
  )
}

export default PerguntasElegibilidade
